import { PlanInterval } from './PlanInterval'
import { PlanType } from './planType'

export interface Plan {
  id: string
  type: PlanType
  /**
   * The billing interval of the plan when it's subscribed (MONTHLY vs YEARLY).
   *
   * Some plans cannot be subscribed to thus they do not have an interval.
   */
  interval?: PlanInterval
  /**
   * The pricing group of the plan.
   *
   * Each plan that has a price is available in a variety of pricing groups so that we can test different prices
   * over time.
   * Only one pricing group can be set at a time, adn only plans that are available in the pricing group will be
   * used for queries that get which plans the user can subscribe to.
   *
   * @see usePlanPricingGroup
   */
  group?: string
  /**
   * Most plans follow a hierarchy where a higher level plan includes all features of the lower level
   * plan, plus some additional ones.
   *
   * Additionally, a YEARLY plan is a higher level than its MONTHLY equivalent since going from
   * YEARLY down to MONTHLY is considered a "downgrade" that gets queued to happen at the end of the
   * billing period, whereas "upgrades" happen instantly.
   *
   * Some plans do not follow this structure and thus don't have a plan level.
   * These plans are typically for reverse trials on signup and are not purchaseable.
   */
  planLevel?: number
  /**
   * If true, new signups are automatically enrolled into this plan via our reverse trial functionality.
   *
   * At most only 1 plan can be set to true at a time.
   */
  autoEnroll: boolean
  /**
   * This is the price of the plan, in cents. For instance, 500 = $5.00
   */
  priceInCents?: number
  /**
   * For plans that have extras, the starting price is what it would cost without any extras selected
   * above the minimums.
   */
  startingPriceInCents?: number
  /**
   * If this plan is cheaper than its complementary plan, this is the percentage
   * of that discount in the range 0.0 to 1.0.
   */
  discountPercent?: number
  /**
   * If this plan is cheaper than its complementary plan, this is the absolute
   * value of the discount.
   */
  discountAmountInCents?: number
  /**
   * For a MONTHLY plan, this is its YEARLY compliment.
   * For a YEARLY plan, this is its MONTHLY compliment.
   */
  complementaryPlanId?: string
  /**
   * List of extras that can be optionally purchased or added to the plan's subscription.
   */
  planExtras?: PlanExtra[]
}

export type PlanExtraType = 'seats' | 'codeapi'

export interface PlanExtra {
  planExtraId: string
  planExtraType: PlanExtraType
  unitPriceInCents: number
  minQuantity: number
  maxQuantity: number
}

export interface PlanExtraComposite extends PlanExtra {
  currentQuantity: number
}

export interface PlanExtraRender extends PlanAddOnProps {
  planExtraType: PlanExtraType
  currentValue: number
  maxValue: number
  minValue: number
}

export interface PlanExtraDetail {
  planExtraId: string
  quantity: number

  planExtra?: PlanExtra
}

export interface PlanExtraResponse {
  planExtraType: PlanExtraType
  priceInCents: number
  planExtraId: string
  quantity: number
}

export const enum PlanPurchaseExperienceMode {
  CHANGE_PAYMENT = 'changepayment',
  PURCHASE_ADDONS = 'purchaseaddons'
}

// Pro Flex
export const ProFlexPlatformSeats = '/icons/pro-flex-platform-seats.svg'
export const ProFlexAPI = '/icons/pro-flex-api.svg'

export interface PlanAddOnProps {
  isCheckbox: boolean
  icon: string
  header: string
  description: string
  priceText: string
  priceTextYearly: string
}

export const PlanExtraName: Record<PlanExtraType, string> = {
  seats: 'Platform seats',
  codeapi: 'API'
}

export type PlanAddOns = Record<PlanExtraType, PlanAddOnProps>

export const ProFlexBasicAddOns: PlanAddOns = {
  seats: {
    isCheckbox: false,
    icon: ProFlexPlatformSeats,
    header: PlanExtraName['seats'],
    description: 'Build and collaborate efficiently when you buy additional seats.',
    priceText: ' per seat, per month',
    priceTextYearly: ' per seat, per year'
  },
  codeapi: {
    isCheckbox: true,
    icon: ProFlexAPI,
    header: PlanExtraName['codeapi'],
    description: 'Create tens of thousands of Flowcodes at scale via our API.',
    priceText: ' per month',
    priceTextYearly: ' per year'
  }
}
